import { observer } from 'mobx-react'
import fp from 'lodash/fp'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Block, Heading } from 'suomifi-ui-components'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import {
  getDisplayValueForReligiousCommunities,
  getDisplayValueForKansalaisuus,
  getDisplayValueForCountryCode,
} from 'todistukset-common/src/vtj/localization.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import InfoItem from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/InfoItem.react'
import { AetOrdererUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { reversedDisplayNameOf } from 'todistukset-common/src/vtj/name.util'

export type AetSummaryOrderInfoProps = Pick<
  AetOrdererUiForm,
  'orderer' | 'keepingCurrentLastName' | 'newLastName'
>

const AetSummaryOrdererInfo: React.FC<AetSummaryOrderInfoProps> = observer(
  ({
    orderer: {
      firstNames,
      lastName,
      hetu,
      maritalStatus,
      numberOfMarriages,
      countryOfResidenceCode,
      religiousCommunities: religiousCommunitiesFromVtj,
      citizenshipCode: citizenshipCodeFromVtj,
    },
    keepingCurrentLastName,
    newLastName,
  }) => {
    const { t } = useTranslation()

    const {
      common: { lang },
    } = useStore()

    const numberOfMarriagesValue = finalEditableFieldValue(numberOfMarriages)
    const maritalStatusValue = finalEditableFieldValue(maritalStatus)

    const numberOfMarriagesStr =
      maritalStatusValue === MaritalStatus.NAIMATON
        ? '0'
        : typeof numberOfMarriagesValue === 'number'
        ? String(numberOfMarriagesValue)
        : '-'

    const displayedReligiousCommunityInfo = Array.isArray(
      religiousCommunitiesFromVtj
    )
      ? getDisplayValueForReligiousCommunities(
          religiousCommunitiesFromVtj,
          lang
        )
      : t('aetOwnInfoNoInformation')

    const displayedCitizenshipInfo = !fp.isEmpty(citizenshipCodeFromVtj)
      ? getDisplayValueForKansalaisuus(citizenshipCodeFromVtj, lang)
      : t('aetOwnInfoNoInformation')

    const countryTranslated =
      countryOfResidenceCode === null
        ? '-'
        : getDisplayValueForCountryCode(
            finalEditableFieldValue(countryOfResidenceCode),
            lang
          )

    const lastNameAfterMarriage = finalEditableFieldValue(
      keepingCurrentLastName
    )
      ? finalEditableFieldValue(lastName) || '-'
      : finalEditableFieldValue(newLastName) || '-'

    return (
      <Block variant="section">
        <Heading variant="h3">
          <LocalizedString stringKey="aetSummaryOrdererInfoTitle" />
        </Heading>
        <LocalizedParagraphs stringKey="aetSummaryOrdererInfoDescription_paragraphs" />
        <Columns>
          <InfoItem
            title={t('aetSummaryNameLabel')}
            content={`${reversedDisplayNameOf({ lastName, firstNames })}`}
          />
          <InfoItem title={t('aetSummaryPersonIdLabel')} content={hetu} />
          <InfoItem
            title={t('aetSummaryCitizenshipLabel')}
            content={displayedCitizenshipInfo}
            testIdPrefix="citizenship"
          />
          <InfoItem
            title={t('aetSummaryMaritalStatusLabel')}
            content={
              maritalStatusValue
                ? t(`maritalStatus_${maritalStatusValue}`)
                : '-'
            }
          />
          <InfoItem
            title={t('aetSummaryNumberOfMarriagesLabel')}
            content={numberOfMarriagesStr}
          />
          <InfoItem
            title={t('aetSummaryReligiousCommunityLabel')}
            content={displayedReligiousCommunityInfo}
            testIdPrefix="religious-community"
          />
          <InfoItem
            title={t('aetSummaryCountryOfResidenceLabel')}
            content={countryTranslated}
            testIdPrefix="country-of-residence"
          />
          <InfoItem
            title={t('aetSummaryLastNameAfterMarriageLabel')}
            content={lastNameAfterMarriage}
          />
        </Columns>
      </Block>
    )
  }
)

export default AetSummaryOrdererInfo
